import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Users, Key, Monitor, Unlock } from 'lucide-react';
import { useTodasLicencasEmUso, useLiberarLicenca } from '@/hooks/useSoftwareLicencas';
import { useSoftwares } from '@/hooks/useSoftwares';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function LicencasUsuariosTab() {
  const { data: licencas, isLoading } = useTodasLicencasEmUso();
  const { data: softwares } = useSoftwares();
  const liberarLicenca = useLiberarLicenca();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [softwareFilter, setSoftwareFilter] = useState<string>('all');
  const [licencaToRelease, setLicencaToRelease] = useState<string | null>(null);

  const filteredLicencas = licencas?.filter((licenca) => {
    const matchesSearch = 
      licenca.usuario?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      licenca.usuario?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      licenca.software?.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSoftware = softwareFilter === 'all' || licenca.software?.id === softwareFilter;
    return matchesSearch && matchesSoftware;
  }) || [];

  // Métricas
  const usuariosUnicos = new Set(licencas?.map(l => l.usuario_id).filter(Boolean)).size;
  const totalLicencasAtribuidas = licencas?.length || 0;
  
  // Top 3 softwares mais utilizados
  const softwareCount = licencas?.reduce((acc, l) => {
    if (l.software?.nome) {
      acc[l.software.nome] = (acc[l.software.nome] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>) || {};
  
  const topSoftwares = Object.entries(softwareCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const handleRelease = () => {
    if (licencaToRelease) {
      liberarLicenca.mutate(licencaToRelease);
      setLicencaToRelease(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usuários com Licenças</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usuariosUnicos}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Licenças Atribuídas</CardTitle>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalLicencasAtribuidas}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Softwares</CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {topSoftwares.length > 0 ? (
                topSoftwares.map(([nome, count], index) => (
                  <div key={nome} className="flex justify-between text-sm">
                    <span className="truncate">{index + 1}. {nome}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))
              ) : (
                <span className="text-muted-foreground text-sm">Nenhum software</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Licenças por Usuário */}
      <Card>
        <CardHeader>
          <CardTitle>Licenças por Usuário</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por usuário ou software..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={softwareFilter} onValueChange={setSoftwareFilter}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="Filtrar por Software" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Softwares</SelectItem>
                {softwares?.map((sw) => (
                  <SelectItem key={sw.id} value={sw.id}>
                    {sw.nome} {sw.versao ? `(${sw.versao})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : filteredLicencas.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma licença atribuída encontrada
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Software</TableHead>
                    <TableHead>Chave</TableHead>
                    <TableHead>Ativo/Máquina</TableHead>
                    <TableHead>Data Atribuição</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLicencas.map((licenca) => (
                    <TableRow key={licenca.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{licenca.usuario?.name || 'N/A'}</div>
                          <div className="text-sm text-muted-foreground">{licenca.usuario?.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{licenca.software?.nome || 'N/A'}</div>
                          {licenca.software?.versao && (
                            <div className="text-sm text-muted-foreground">{licenca.software.versao}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {licenca.chave_licenca ? (
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {licenca.chave_licenca.substring(0, 16)}...
                          </code>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {licenca.ativo?.nome || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        {licenca.data_atribuicao 
                          ? new Date(licenca.data_atribuicao).toLocaleDateString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setLicencaToRelease(licenca.id)}
                          title="Liberar Licença"
                        >
                          <Unlock className="h-4 w-4 mr-1" />
                          Liberar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Confirmação */}
      <AlertDialog open={!!licencaToRelease} onOpenChange={(open) => !open && setLicencaToRelease(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Liberar Licença</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja liberar esta licença? O usuário perderá o acesso ao software.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRelease}>
              Liberar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
